import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PagedAuditResponse } from './+models/audit-log.model';
import {
  PostedFlightSkeleton,
  PagedPostedFlightAudit,
  PagedFlightCrewMemberAudit,
  PagedLogbookApproachAudit,
  PagedFlightPassengerAudit,
  PagedExpenseAudit,
  PagedPostedFlightEngineAudit,
  PagedPostedFlightComponentAudit,
  PagedPostedFlightFuelAudit,
  PagedPostedFlightAdditionalDetailAudit,
  PagedPostedFlightDeAntiIceAudit,
  PagedPostedFlightNotesAudit,
  PagedEngineOilFluidAudit,
  PagedComponentOilFluidAudit,
  PagedOilFluidAudit,
  PagedFuelUpliftAudit,
  PagedBusinessCategoryAudit,
  PagedDepartmentAudit,
  PagedLogbookAudit,
  PagedCrewMemberDutyAudit,
  PagedAirFrameLandingsAudit,
  PagedEngineCyclesAudit,
  PagedVORCheckDataAudit,
  PreFlightChecklistUserESignaturesAudit,
  PostedFlightChecklistUserESignaturesAudit,
  FlightLogUserESignaturesAudit,
  SquawkUserESignaturesAudit,
  PagedFlightBusinessPersonAudit
} from './+models/posted-flight.audit.model';

@Injectable()
export class PostedFlightAuditService {
  private readonly apiUrl = '/sdapi/post-flight/PostedFlightAudit';

  constructor(private readonly http: HttpClient) {}

  getPostedFlightSkeleton(postedFlightId: number) {
    return this.http.get<PostedFlightSkeleton>(
      `${this.apiUrl}/PostedFlightSkeleton/${postedFlightId}`
    );
  }

  getPostedFlightAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedPostedFlightAudit>(
      `${postedFlightId}/LegDetails`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightCrewMemberAudit(
    flightCrewMemberId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightCrewMemberAudit>(
      `FlightCrewMember/${flightCrewMemberId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightBusinessPersonAudit(
    flightBusinessPersonId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightBusinessPersonAudit>(
      `FlightBusinessPerson/${flightBusinessPersonId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getLogbookApproachAudit(
    logbookApproachId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedLogbookApproachAudit>(
      `LogbookApproach/${logbookApproachId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightPassengerAudit(
    flightPassengerId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFlightPassengerAudit>(
      `FlightPassenger/${flightPassengerId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getExpenseAudit(
    expenseId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedExpenseAudit>(
      `Expense/${expenseId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getPostedFlightEngineAudit(
    postedFlightEngineId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedPostedFlightEngineAudit>(
      `PostedFlightEngine/${postedFlightEngineId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getPostedFlightComponentAudit(
    postedFlightComponentId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedPostedFlightComponentAudit>(
      `PostedFlightComponent/${postedFlightComponentId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getPostedFlightFuelAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedPostedFlightFuelAudit>(
      `${postedFlightId}/Fuel`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getPostedFlightAdditionalDetailAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedPostedFlightAdditionalDetailAudit>(
      `${postedFlightId}/AdditionalLegDetails`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getOilFluidAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedOilFluidAudit>(
      `${postedFlightId}/OilsAndFluids`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getEngineOilFluidAudit(
    postedFlightEngineId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedEngineOilFluidAudit>(
      `PostedFlightEngine/${postedFlightEngineId}/OilsAndFluids`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getComponentOilFluidAudit(
    postedFlightComponentId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedComponentOilFluidAudit>(
      `PostedFlightComponent/${postedFlightComponentId}/OilsAndFluids`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getPostedFlightDeAntiIceAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedPostedFlightDeAntiIceAudit>(
      `${postedFlightId}/DeAntiIce`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getPostedFlightNotesAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedPostedFlightNotesAudit>(
      `${postedFlightId}/Notes`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFuelUpliftAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedFuelUpliftAudit>(
      `${postedFlightId}/FuelUplift`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getBusinessCategoryAudits(
    flightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedBusinessCategoryAudit>(
      `Flight/${flightId}/BusinessCategory`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getDepartmentAudits(
    flightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedDepartmentAudit>(
      `Flight/${flightId}/Department`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getLogbookAudits(
    logbookId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedLogbookAudit>(
      `Logbook/${logbookId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getCrewMemberDutyAudits(
    crewMemberId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedCrewMemberDutyAudit>(
      `CrewMemberDuty/${crewMemberId}`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }
  getAirFrameLandingsAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedAirFrameLandingsAudit>(
      `${postedFlightId}/AirframeLandings`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getEngineCyclesAudit(
    postedFlightEngineId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedEngineCyclesAudit>(
      `PostedFlightEngine/${postedFlightEngineId}/Cycles`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }
  getVORCheckDataAudit(
    aircraftProfileId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PagedVORCheckDataAudit>(
      `AircraftProfile/${aircraftProfileId}/VORCheckData`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getPreFlightChecklistUserESignaturesAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PreFlightChecklistUserESignaturesAudit>(
      `${postedFlightId}/PreFlightChecklistUserESignatures`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getPostedFlightChecklistUserESignaturesAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<PostedFlightChecklistUserESignaturesAudit>(
      `${postedFlightId}/PostedFlightChecklistUserESignatures`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getFlightLogUserESignaturesAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<FlightLogUserESignaturesAudit>(
      `${postedFlightId}/FlightLogUserESignatures`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  getSquawkUserESignaturesAudit(
    postedFlightId: number,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    return this.getAuditPage<SquawkUserESignaturesAudit>(
      `${postedFlightId}/SquawkUserESignatures`,
      pageNumber,
      pageSize,
      lastAuditLogId
    );
  }

  private getAuditPage<T extends PagedAuditResponse>(
    subRoute: string,
    pageNumber?: number,
    pageSize?: number,
    lastAuditLogId?: number | null
  ) {
    if (!pageNumber) {
      pageNumber = 1;
    }
    if (!pageSize) {
      pageSize = 10;
    }
    let url = `${this.apiUrl}/${subRoute}?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (lastAuditLogId) {
      url = `${url}&lastAuditLogId=${lastAuditLogId}`;
    }

    return this.http.get<T>(url);
  }
}
